/**
 * Thin client for space_invader_gm session APIs (Option B).
 */
import { ARCADE, API_PATH_PROXY_ENABLED, API_ENDPOINTS } from '../../../utils/config';
import { acquireGmLease, endGmSession, noteGmState } from '../gmLease';

/** Lease key for this game's shared GM (game_engines/gm_registry.py). */
export const GM_GAME_ID = 'space_invader';

export const SPACE_INVADER_GM_BASE = API_PATH_PROXY_ENABLED
    ? (ARCADE.space_invader?.API_BASE || '')
    : '/space';

export const INVADER_POINTS = { fast: 60, medium: 40, slow: 20 };

async function parseJson(res) {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(data.error || `Swarm Defense GM HTTP ${res.status}`);
        err.status = res.status;
        err.data = data;
        throw err;
    }
    return noteGmState(GM_GAME_ID, data);
}

/**
 * Ask the DB service for a server-picked seed plus a single-use signed token
 * (registered users only). The run is ranked only when it is played on that
 * seed and posted with the token. Returns null for guests or on any failure:
 * the game still starts, on a GM-picked seed, and saves as unranked.
 */
export async function fetchSeedToken(authToken) {
    if (!authToken) return null;
    try {
        const res = await fetch(API_ENDPOINTS.SEED_TOKEN, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
            body: JSON.stringify({ game_id: GM_GAME_ID }),
        });
        if (!res.ok) return null;
        const data = await res.json().catch(() => null);
        if (!data || !Number.isInteger(data.seed) || typeof data.seed_token !== 'string') return null;
        return { seed: data.seed, seedToken: data.seed_token };
    } catch {
        return null;
    }
}

export async function createSpaceInvaderSession({ seed } = {}) {
    await acquireGmLease(GM_GAME_ID);
    const res = await fetch(`${SPACE_INVADER_GM_BASE}/session/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: Number.isInteger(seed) ? JSON.stringify({ seed }) : '{}',
    });
    return parseJson(res);
}

export async function moveSpaceInvaderSession(sessionId, action = 'stay') {
    const res = await fetch(`${SPACE_INVADER_GM_BASE}/session/${sessionId}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
    });
    return parseJson(res);
}

export async function deleteSpaceInvaderSession(sessionId) {
    if (!sessionId) return;
    endGmSession(GM_GAME_ID, sessionId);
    try {
        await fetch(`${SPACE_INVADER_GM_BASE}/session/${sessionId}/delete`, { method: 'POST' });
    } catch {
        /* best-effort */
    }
}
